import { ACTION_LABEL } from "@/lib/i18nLabels";
import { useState } from "react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { DialogFooter } from "@/components/ui/dialog";
import SearchableSelect from "@/components/SearchableSelect";
import RupiahInput from "@/components/RupiahInput";
import PPNBreakdown from "@/components/PPNBreakdown";
import api, { formatDetail } from "@/lib/api";

const emptyVO = { parent_project_id: "", variation_order_name: "", vo_start_date: "", vo_end_date: "", contract_value: "", retention_pct: 0, retention_period_days: 0, retention_type: "inside", is_ppn_exempt: false };
const toIso = (d) => (d ? new Date(d).toISOString() : null);

export default function VariationOrderForm({ generalParents, onDone, onCancel }) {
  const [vo, setVo] = useState(emptyVO);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!vo.parent_project_id) { toast.error("Please select a parent project"); return; }
    setBusy(true);
    try {
      await api.post("/active-projects", {
        project_type: "variation_order",
        parent_project_id: vo.parent_project_id,
        variation_order_name: vo.variation_order_name,
        vo_start_date: toIso(vo.vo_start_date),
        vo_end_date: toIso(vo.vo_end_date),
        contract_value: vo.contract_value ? parseFloat(vo.contract_value) : null,
        retention_pct: parseFloat(vo.retention_pct) || 0,
        retention_period_days: parseInt(vo.retention_period_days, 10) || 0,
        retention_type: vo.retention_type || "inside",
      });
      toast.success("Variation Order created");
      onDone?.();
    } catch (err) { toast.error(formatDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} className="space-y-4">
      <div className="space-y-1.5">
        <Label className="overline text-slate-600">Parent Project</Label>
        {/* 149-cw-r178: parent project bisa diketik utk mencari. */}
        <SearchableSelect
          testid="vo-parent-select"
          value={vo.parent_project_id}
          onChange={(v) => setVo({ ...vo, parent_project_id: v })}
          placeholder="Select an existing project…"
          searchPlaceholder="Ketik nama project / client…"
          emptyText="Belum ada project general"
          options={generalParents.map((p) => ({ value: p.id, label: `${p.project_name}${p.client ? ` · ${p.client}` : ""}` }))}
        />
      </div>
      <div className="space-y-1.5">
        <Label className="overline text-slate-600">Variation Order Name</Label>
        <Input data-testid="vo-name" required value={vo.variation_order_name} onChange={(e) => setVo({ ...vo, variation_order_name: e.target.value })} className="rounded-none" placeholder="e.g. VO-01 Additional Piping" />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label className="overline text-slate-600">VO Contract Issue Date</Label>
          <Input data-testid="vo-start" type="date" required value={vo.vo_start_date} onChange={(e) => setVo({ ...vo, vo_start_date: e.target.value })} className="rounded-none" />
        </div>
        <div className="space-y-1.5">
          <Label className="overline text-slate-600">VO End Date <span className="normal-case text-slate-400 text-[9px]">(opsional)</span></Label>
          <Input data-testid="vo-end" type="date" value={vo.vo_end_date} onChange={(e) => setVo({ ...vo, vo_end_date: e.target.value })} className="rounded-none" />
        </div>
      </div>
      <div className="space-y-1.5">
        <Label className="overline text-slate-600">VO Contract Value (IDR)</Label>
        <RupiahInput testid="vo-contract" value={vo.contract_value} onChange={(v) => setVo({ ...vo, contract_value: v })} />
        <label className="flex items-center gap-1.5 text-[10px] cursor-pointer select-none" data-testid="vo-non-ppn-toggle">
          <input type="checkbox" checked={!!vo.is_ppn_exempt} onChange={(e) => setVo({ ...vo, is_ppn_exempt: e.target.checked })} className="h-3 w-3 accent-[#FF5A1F]" />
          <span className="uppercase tracking-wider font-semibold text-slate-600">Project Non-PPN (bebas PPN 11%)</span>
        </label>
        <PPNBreakdown value={vo.contract_value} isExempt={!!vo.is_ppn_exempt} testid="vo-ppn-breakdown" />
        <p className="text-[10px] text-slate-500 tabular-nums">
          {vo.contract_value ? new Intl.NumberFormat("id-ID", { style: "currency", currency: "IDR", maximumFractionDigits: 0 }).format(parseFloat(vo.contract_value) || 0) : ""}
        </p>
      </div>
      <div className="space-y-1.5">
        <Label className="overline text-slate-600">Retention</Label>
        <div className="inline-flex border border-slate-300 text-xs w-full" data-testid="vo-retention">
          {[{ v: 0, label: "None" }, { v: 5, label: "5%" }, { v: 10, label: "10%" }].map((r) => (
            <button key={r.v} type="button" data-testid={`vo-retention-${r.v}`} onClick={() => setVo({ ...vo, retention_pct: r.v })}
              className={`flex-1 px-3 py-2 uppercase tracking-wider font-semibold ${vo.retention_pct === r.v ? "bg-[#0B1D3A] text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>
              {r.label}
            </button>
          ))}
        </div>
      </div>
      {Number(vo.retention_pct) > 0 && (
        <div className="space-y-1.5">
          <Label className="overline text-slate-600">Retention Period (Masa Retensi)</Label>
          <div className="relative">
            <Input data-testid="vo-retention-period-days" type="number" min="0" max="3650" step="1"
              value={vo.retention_period_days ?? 0}
              onChange={(e) => setVo({ ...vo, retention_period_days: e.target.value })}
              className="rounded-none pr-14 tabular-nums" placeholder="0" />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 text-xs">Hari</span>
          </div>
          <div className="flex flex-wrap gap-1">
            {[30, 90, 180, 365].map((d) => (
              <button key={d} type="button" data-testid={`vo-retention-preset-${d}`}
                onClick={() => setVo({ ...vo, retention_period_days: d })}
                className={`text-[10px] px-2 py-0.5 border transition-colors ${Number(vo.retention_period_days) === d ? "bg-[#0B1D3A] text-white border-[#0B1D3A]" : "border-slate-300 text-slate-600 hover:border-[#FF5A1F] hover:text-[#FF5A1F]"}`}>
                {d === 365 ? "1 Tahun" : `${d} Hari`}
              </button>
            ))}
          </div>
          <p className="text-[10px] text-slate-500">
            Ketik durasi dalam <span className="font-semibold">hari</span> (0 = belum ditentukan).
            {Number(vo.retention_period_days) > 0 && (
              <span className="ml-1 text-amber-700 font-mono">≈ {(Number(vo.retention_period_days) / 30).toFixed(1)} bulan</span>
            )}
          </p>
        </div>
      )}
      <DialogFooter>
        <Button type="button" variant="outline" className="rounded-none" onClick={onCancel}>{ACTION_LABEL.cancel}</Button>
        <Button type="submit" data-testid="vo-submit" disabled={busy} className="rounded-none bg-[#FF5A1F] hover:bg-[#e04a13]">
          {busy ? "Saving…" : "Save Variation Order"}
        </Button>
      </DialogFooter>
    </form>
  );
}
